import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Typography,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  FormControlLabel,
  Switch,
  Tooltip
} from '@mui/material';
import {
  CloudUpload,
  Image as ImageIcon,
  Delete,
  CheckCircle,
  Error as ErrorIcon,
  MyLocation
} from '@mui/icons-material';
import imageCompression from 'browser-image-compression';
import FarmRoadImageService from './FarmRoadImageService';

const ImageUploadZone = ({
  farmId,
  farmName,
  sectors = [],
  compartments = [],
  culture = 'framboises',
  onUploadComplete,
  onAnalysisComplete,
  activeImageUrl = null,
  onBeforeUpload
}) => {
  // Ensure sectors and compartments are always arrays
  const safeSectors = Array.isArray(sectors) ? sectors : [];
  const safeCompartments = Array.isArray(compartments) ? compartments : [];

  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedSector, setSelectedSector] = useState('');
  const [selectedCompartment, setSelectedCompartment] = useState('');
  const [notes, setNotes] = useState('');
  const [gpsCoordinates, setGpsCoordinates] = useState({ latitude: null, longitude: null });
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showNewUpload, setShowNewUpload] = useState(false);
  const [advancedAnalysis, setAdvancedAnalysis] = useState(false);

  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // Supported image types
  const SUPPORTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  /**
   * Validate image file
   */
  const validateImage = (file) => {
    if (!SUPPORTED_TYPES.includes(file.type)) {
      throw new Error('Format non supporté. Utilisez JPG, PNG ou WebP.');
    }

    if (file.size > MAX_FILE_SIZE) {
      throw new Error('Fichier trop volumineux. Maximum 10 MB.');
    }

    return true;
  };

  /**
   * Compress image before upload
   */
  const compressImage = async (file) => {
    try {
      const options = {
        maxSizeMB: 2,
        maxWidthOrHeight: 2048,
        useWebWorker: true,
        fileType: file.type
      };

      const compressedFile = await imageCompression(file, options);
      console.log(`Image compressed: ${(file.size / 1024 / 1024).toFixed(2)} MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);

      return compressedFile;
    } catch (error) {
      console.error('Compression error:', error);
      return file; // Return original if compression fails
    }
  };

  /**
   * Handle file selection
   */
  const handleFileSelect = async (file) => {
    try {
      setError(null);
      setSuccess(null);

      validateImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      setSelectedImage(file);
    } catch (err) {
      setError(err.message);
      setSelectedImage(null);
      setImagePreview(null);
    }
  };

  /**
   * Handle drag events
   */
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, []);

  /**
   * Handle file input change
   */
  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  /**
   * Get GPS location
   */
  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      setError('Géolocalisation non supportée par ce navigateur.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsCoordinates({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setSuccess('Position GPS capturée avec succès.');
      },
      (error) => {
        setError(`Erreur de géolocalisation: ${error.message}`);
      }
    );
  };

  /**
   * Upload and analyze image
   */
  const handleUploadAndAnalyze = async () => {
    if (!selectedImage) {
      setError('Veuillez sélectionner une image.');
      return;
    }

    if (!selectedSector) {
      setError('Veuillez sélectionner un secteur.');
      return;
    }

    try {
      // Informer le parent pour masquer l'ancienne analyse
      if (onBeforeUpload) {
        try { onBeforeUpload(); } catch (e) { console.warn('onBeforeUpload error', e); }
      }

      setUploading(true);
      setError(null);
      setSuccess(null);
      setUploadProgress(0);

      // Compress image
      const compressedImage = await compressImage(selectedImage);

      // Find sector and compartment details (support both sectorId and id)
      const sector = safeSectors.find(s => (s.sectorId || s.id) === selectedSector);
      const compartment = safeCompartments.find(c => (c.compartmentId || c.id) === selectedCompartment);

      // Prepare metadata
      const metadata = {
        farmId,
        farmName,
        sectorId: selectedSector,
        sectorName: sector?.name || selectedSector,
        compartmentId: selectedCompartment || null,
        compartmentName: compartment?.name || null,
        gpsLatitude: gpsCoordinates.latitude,
        gpsLongitude: gpsCoordinates.longitude,
        notes,
        culture
      };

      // Upload with progress
      const uploadResult = await FarmRoadImageService.uploadImageWithProgress(
        compressedImage,
        metadata,
        (progress) => {
          setUploadProgress(progress);
        }
      );

      if (onUploadComplete) {
        onUploadComplete(uploadResult);
      }

      setSuccess(`Image téléchargée avec succès (ID: ${uploadResult.imageId})`);
      setUploading(false);
      setUploadProgress(100);

      // Start AI analysis
      setAnalyzing(true);

      const analysisResult = await FarmRoadImageService.analyzeImage(uploadResult.imageId, {
        analysisType: 'comprehensive',
        includeRecommendations: true,
        correlateWithSensors: true,
        advanced: advancedAnalysis
      });

      setAnalyzing(false);

      if (onAnalysisComplete) {
        onAnalysisComplete({
          ...uploadResult,
          analysis: analysisResult.analysis  // Extraire l'objet analysis du résultat
        });
      }

      setSuccess('Analyse IA terminée avec succès !');

      // NE PAS réinitialiser automatiquement - l'utilisateur peut continuer à examiner l'image
      // Ajouter un bouton "Nouvelle analyse" pour permettre de recommencer

    } catch (err) {
      console.error('Upload/Analysis error:', err);
      setError(err.message || 'Erreur lors du téléchargement ou de l\'analyse.');
      setUploading(false);
      setAnalyzing(false);
    }
  };

  /**
   * Reset form
   */
  const resetForm = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setSelectedSector('');
    setSelectedCompartment('');
    setNotes('');
    setGpsCoordinates({ latitude: null, longitude: null });
    setUploadProgress(0);
    setError(null);
    setSuccess(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Card elevation={3}>
      <CardContent>
        {/* Title - Responsive version */}
        <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon />
            Télécharger une image
          </Typography>
        </Box>
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon />
            Télécharger une image pour analyse IA
          </Typography>
        </Box>

        {/* Error/Success alerts */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Drag & Drop Zone */}
        <Box
          ref={dropZoneRef}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          sx={{
            border: '2px dashed',
            borderColor: isDragging ? 'primary.main' : 'grey.300',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            bgcolor: isDragging ? 'action.hover' : 'background.default',
            cursor: 'pointer',
            transition: 'all 0.3s',
            mb: 3,
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover'
            }
          }}
          onClick={() => { if (showNewUpload || !activeImageUrl) fileInputRef.current?.click(); }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />

          {!imagePreview && activeImageUrl && !showNewUpload ? (
            <Box>
              <img
                src={activeImageUrl}
                alt="Dernière image analysée"
                style={{
                  maxWidth: '100%',
                  maxHeight: 300,
                  borderRadius: 8,
                  objectFit: 'contain'
                }}
              />
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                <Button variant="outlined" onClick={() => setShowNewUpload(true)} startIcon={<CloudUpload />}>
                  Nouvelle analyse
                </Button>
              </Box>
            </Box>
          ) : !imagePreview ? (
            <>
              <CloudUpload sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Glissez-déposez une image ici
              </Typography>
              <Typography variant="body2" color="text.secondary">
                ou cliquez pour sélectionner
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Formats supportés: JPG, PNG, WebP (max 10 MB)
              </Typography>
            </>
          ) : (
            <Box>
              <img
                src={imagePreview}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: 300,
                  borderRadius: 8,
                  objectFit: 'contain'
                }}
              />
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                <Chip
                  label={selectedImage?.name}
                  onDelete={resetForm}
                  deleteIcon={<Delete />}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Box>
          )}
        </Box>

        {/* Sector Selection */}
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth required>
            <InputLabel>Secteur / Serre</InputLabel>
            <Select
              value={selectedSector}
              onChange={(e) => {
                const sector = safeSectors.find(s => (s.sectorId || s.id) === e.target.value);
                setSelectedSector(e.target.value);
                // Auto-set compartment if sector has one
                if (sector && (sector.compartmentId || sector.compartment_id)) {
                  setSelectedCompartment(sector.compartmentId || sector.compartment_id);
                }
              }}
              label="Secteur / Serre"
              disabled={uploading || analyzing}
            >
              {safeSectors.length === 0 && (
                <MenuItem disabled>
                  <em>Aucun secteur disponible - Synchronisation en cours...</em>
                </MenuItem>
              )}
              {safeSectors.map((sector) => {
                const sectorId = sector.sectorId || sector.id;
                const sectorName = sector.name || sectorId;
                return (
                  <MenuItem key={sectorId} value={sectorId}>
                    {sectorName}
                    {sector.compartmentName && ` - ${sector.compartmentName}`}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {safeSectors.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              💡 La liste des secteurs se charge automatiquement depuis votre ferme
            </Typography>
          )}
        </Box>

        {/* GPS Coordinates */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <TextField
            fullWidth
            label="Latitude GPS"
            value={gpsCoordinates.latitude || ''}
            onChange={(e) => setGpsCoordinates(prev => ({ ...prev, latitude: parseFloat(e.target.value) || null }))}
            type="number"
            inputProps={{ step: '0.000001' }}
            disabled={uploading || analyzing}
          />
          <TextField
            fullWidth
            label="Longitude GPS"
            value={gpsCoordinates.longitude || ''}
            onChange={(e) => setGpsCoordinates(prev => ({ ...prev, longitude: parseFloat(e.target.value) || null }))}
            type="number"
            inputProps={{ step: '0.000001' }}
            disabled={uploading || analyzing}
          />
          <IconButton
            onClick={handleGetLocation}
            color="primary"
            disabled={uploading || analyzing}
            title="Obtenir ma position"
          >
            <MyLocation />
          </IconButton>
        </Box>

        {/* Notes */}
        <TextField
          fullWidth
          label="Notes (optionnel)"
          placeholder="Ex: Feuilles jaunissantes observées, secteur nord"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          multiline
          rows={2}
          disabled={uploading || analyzing}
          sx={{ mb: 2 }}
        />

        {/* Upload Progress */}
        {(uploading || analyzing) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {uploading ? `Téléchargement en cours... ${uploadProgress}%` : 'Analyse IA en cours...'}
            </Typography>
            <LinearProgress
              variant={uploading ? "determinate" : "indeterminate"}
              value={uploadProgress}
              sx={{ height: 8, borderRadius: 1 }}
            />
          </Box>
        )}

        {/* Action Buttons with Analysis Mode */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' }
        }}>
          {/* Analysis Mode Toggle - Left */}
          <Tooltip
            title={advancedAnalysis
              ? "Analyse détaillée avec Gemini 2.5 Pro (plus lent, qualité supérieure)"
              : "Analyse rapide avec Gemini 2.5 Flash (recommandé)"
            }
            arrow
          >
            <FormControlLabel
              control={
                <Switch
                  checked={advancedAnalysis}
                  onChange={(e) => setAdvancedAnalysis(e.target.checked)}
                  disabled={uploading || analyzing}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    Analyse avancée
                  </Typography>
                  {advancedAnalysis ? (
                    <Chip label="Pro 🎯" size="small" color="secondary" />
                  ) : (
                    <Chip label="Flash ⚡" size="small" color="primary" />
                  )}
                </Box>
              }
              sx={{ m: 0 }}
            />
          </Tooltip>

          {/* Buttons - Right */}
          <Box sx={{
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            width: { xs: '100%', sm: 'auto' }
          }}>
            <Button
              variant="outlined"
              onClick={resetForm}
              disabled={uploading || analyzing || !selectedImage}
              startIcon={<Delete />}
              fullWidth={{ xs: true, sm: false }}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={handleUploadAndAnalyze}
              disabled={uploading || analyzing || !selectedImage || !selectedSector}
              startIcon={uploading || analyzing ? null : <CheckCircle />}
              fullWidth={{ xs: true, sm: false }}
            >
              {uploading ? 'Téléchargement...' : analyzing ? 'Analyse en cours...' : 'Télécharger & Analyser'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ImageUploadZone;
